export interface McGrawHillBook {
  slug: string;
  title: string;
  /** A file under `public/nursing-demo/covers/`; a placeholder until then. */
  coverSrc?: string;
  /** The one book whose Select does anything. */
  isWorking?: boolean;
}

export interface McGrawHillCategory {
  /** URL segment: `/mh/<slug>`. */
  slug: string;
  label: string;
  books: readonly McGrawHillBook[];
}

export const WORKING_BOOK_SLUG = 'katzungs-basic-and-clinical-pharmacology';

// Covers live in public/nursing-demo/covers/<category>/<slug>.jpg; titles are
// as printed on each cover. Veterinary titles are still placeholders.
// Only the working book (Katzung, in Pharmacy) does anything when selected.
export const MCGRAW_HILL_CATEGORIES: readonly McGrawHillCategory[] = [
  {
    slug: 'medicine',
    label: 'Medicine',
    books: [
      {
        slug: 'external-ventricular-and-lumbar-drains',
        title: 'External Ventricular and Lumbar Drains',
        coverSrc:
          '/nursing-demo/covers/medicine/external-ventricular-and-lumbar-drains.jpg',
      },
      {
        slug: 'laposatas-laboratory-medicine',
        title: "Laposata's Laboratory Medicine",
        coverSrc:
          '/nursing-demo/covers/medicine/laposatas-laboratory-medicine.jpg',
      },
      {
        slug: 'the-us-healthcare-ecosystem',
        title: 'The U.S. Healthcare Ecosystem',
        coverSrc:
          '/nursing-demo/covers/medicine/the-us-healthcare-ecosystem.jpg',
      },
      {
        slug: 'murtaghs-general-practice',
        title: "Murtagh's General Practice",
        coverSrc: '/nursing-demo/covers/medicine/murtaghs-general-practice.jpg',
      },
      {
        slug: 'smiths-patient-centered-interviewing',
        title: "Smith's Patient-Centered Interviewing",
        coverSrc:
          '/nursing-demo/covers/medicine/smiths-patient-centered-interviewing.jpg',
      },
      {
        slug: 'fundamentals-of-virology',
        title: 'Fundamentals of Virology',
        coverSrc: '/nursing-demo/covers/medicine/fundamentals-of-virology.jpg',
      },
      {
        slug: 'ganongs-review-of-medical-physiology',
        title: "Ganong's Review of Medical Physiology",
        coverSrc:
          '/nursing-demo/covers/medicine/ganongs-review-of-medical-physiology.jpg',
      },
      {
        slug: 'case-files-family-medicine',
        title: 'Case Files: Family Medicine',
        coverSrc:
          '/nursing-demo/covers/medicine/case-files-family-medicine.jpg',
      },
      {
        slug: 'harrisons-guide-to-inpatient-medicine',
        title: "Harrison's Guide to Inpatient Medicine",
        coverSrc:
          '/nursing-demo/covers/medicine/harrisons-guide-to-inpatient-medicine.jpg',
      },
      {
        slug: 'current-medical-diagnosis-and-treatment-2027',
        title: 'CURRENT Medical Diagnosis & Treatment 2027',
        coverSrc:
          '/nursing-demo/covers/medicine/current-medical-diagnosis-and-treatment-2027.jpg',
      },
      {
        slug: 'jawetz-melnick-adelbergs-medical-microbiology',
        title: "Jawetz, Melnick & Adelberg's Medical Microbiology",
        coverSrc:
          '/nursing-demo/covers/medicine/jawetz-melnick-adelbergs-medical-microbiology.jpg',
      },
      {
        slug: 'case-files-psychiatry',
        title: 'Case Files: Psychiatry',
        coverSrc: '/nursing-demo/covers/medicine/case-files-psychiatry.jpg',
      },
      {
        slug: 'current-diagnosis-and-treatment-family-medicine',
        title: 'CURRENT Diagnosis & Treatment: Family Medicine',
        coverSrc:
          '/nursing-demo/covers/medicine/current-diagnosis-and-treatment-family-medicine.jpg',
      },
      {
        slug: 'hazzards-geriatric-medicine-and-gerontology',
        title: "Hazzard's Geriatric Medicine and Gerontology",
        coverSrc:
          '/nursing-demo/covers/medicine/hazzards-geriatric-medicine-and-gerontology.jpg',
      },
      {
        slug: 'essentials-of-clinical-nutrition-in-healthcare',
        title: 'Essentials of Clinical Nutrition in Healthcare',
        coverSrc:
          '/nursing-demo/covers/medicine/essentials-of-clinical-nutrition-in-healthcare.jpg',
      },
    ],
  },
  {
    slug: 'nursing',
    label: 'Nursing',
    books: [
      {
        slug: 'critical-care-nursing-certification',
        title: 'Critical Care Nursing Certification',
        coverSrc:
          '/nursing-demo/covers/nursing/critical-care-nursing-certification.jpg',
      },
      {
        slug: 'nursing-school-entrance-exams',
        title: 'Nursing School Entrance Exams',
        coverSrc:
          '/nursing-demo/covers/nursing/nursing-school-entrance-exams.jpg',
      },
      {
        slug: 'progressive-care-nursing-certification',
        title: 'Progressive Care Nursing Certification',
        coverSrc:
          '/nursing-demo/covers/nursing/progressive-care-nursing-certification.jpg',
      },
      {
        slug: 'successful-transition-to-practice',
        title: 'Successful Transition to Practice',
        coverSrc:
          '/nursing-demo/covers/nursing/successful-transition-to-practice.jpg',
      },
      {
        slug: 'aacn-essentials-of-critical-care-nursing',
        title: 'AACN Essentials of Critical Care Nursing',
        coverSrc:
          '/nursing-demo/covers/nursing/aacn-essentials-of-critical-care-nursing.jpg',
      },
      {
        // The first working book; display only now. No cover image yet, so it
        // shows the placeholder.
        slug: 'clinical-nursing-skills-essentials',
        title: 'Clinical Nursing Skills Essentials',
      },
      {
        slug: 'principles-of-critical-care',
        title: "Hall, Schmidt, & Wood's Principles of Critical Care",
        coverSrc:
          '/nursing-demo/covers/nursing/principles-of-critical-care.jpg',
      },
      {
        slug: 'aacn-essentials-of-progressive-care-nursing',
        title: 'AACN Essentials of Progressive Care Nursing',
        coverSrc:
          '/nursing-demo/covers/nursing/aacn-essentials-of-progressive-care-nursing.jpg',
      },
      {
        slug: 'critical-care-nursing-demystified',
        title: 'Critical Care Nursing DeMYSTiFieD',
        coverSrc:
          '/nursing-demo/covers/nursing/critical-care-nursing-demystified.jpg',
      },
      {
        slug: 'essentials-of-nursing-informatics',
        title: 'Essentials of Nursing Informatics',
        coverSrc:
          '/nursing-demo/covers/nursing/essentials-of-nursing-informatics.jpg',
      },
      {
        slug: 'certification-review-for-nurse-anesthesia',
        title: 'Certification Review for Nurse Anesthesia',
        coverSrc:
          '/nursing-demo/covers/nursing/certification-review-for-nurse-anesthesia.jpg',
      },
    ],
  },
  {
    slug: 'pharmacy',
    label: 'Pharmacy',
    books: [
      {
        slug: 'dipiros-pharmacotherapy',
        title: "DiPiro's Pharmacotherapy",
        coverSrc: '/nursing-demo/covers/pharmacy/dipiros-pharmacotherapy.jpg',
      },
      {
        slug: 'goodman-and-gilmans-pharmacological-basis-of-therapeutics',
        title: "Goodman & Gilman's The Pharmacological Basis of Therapeutics",
        coverSrc:
          '/nursing-demo/covers/pharmacy/goodman-and-gilmans-pharmacological-basis-of-therapeutics.jpg',
      },
      {
        // The one working book: its exam holds the uploaded chapters.
        slug: WORKING_BOOK_SLUG,
        title: "Katzung's Basic and Clinical Pharmacology, Sixteenth Edition",
        coverSrc:
          '/nursing-demo/covers/pharmacy/katzungs-basic-and-clinical-pharmacology.jpg',
        isWorking: true,
      },
      {
        slug: 'schwinghammers-pharmacotherapy-casebook',
        title: "Schwinghammer's Pharmacotherapy Casebook",
        coverSrc:
          '/nursing-demo/covers/pharmacy/schwinghammers-pharmacotherapy-casebook.jpg',
      },
      {
        slug: 'community-pharmacy-practice-guidebook',
        title: 'Community Pharmacy Practice Guidebook',
        coverSrc:
          '/nursing-demo/covers/pharmacy/community-pharmacy-practice-guidebook.jpg',
      },
      {
        slug: 'medical-terminology-for-pharmacy-flash-cards',
        title: 'Medical Terminology for Pharmacy Flash Cards',
        coverSrc:
          '/nursing-demo/covers/pharmacy/medical-terminology-for-pharmacy-flash-cards.jpg',
      },
      {
        slug: 'integrated-critical-thinking-in-pharmacy-practice',
        title:
          'Integrated Critical Thinking and Clinical Reasoning in Pharmacy Practice',
        coverSrc:
          '/nursing-demo/covers/pharmacy/integrated-critical-thinking-in-pharmacy-practice.jpg',
      },
      {
        slug: 'pharmaceutics-for-pharmacy-students',
        title: 'Pharmaceutics for Pharmacy Students',
        coverSrc:
          '/nursing-demo/covers/pharmacy/pharmaceutics-for-pharmacy-students.jpg',
      },
      {
        slug: 'top-300-pharmacy-drug-cards',
        title: 'Top 300 Pharmacy Drug Cards',
        coverSrc:
          '/nursing-demo/covers/pharmacy/top-300-pharmacy-drug-cards.jpg',
      },
      {
        slug: 'drug-information-a-guide-for-pharmacists',
        title: 'Drug Information: A Guide for Pharmacists',
        coverSrc:
          '/nursing-demo/covers/pharmacy/drug-information-a-guide-for-pharmacists.jpg',
      },
      {
        slug: 'pharmacotherapy-principles-and-practice',
        title: 'Pharmacotherapy Principles and Practice',
        coverSrc:
          '/nursing-demo/covers/pharmacy/pharmacotherapy-principles-and-practice.jpg',
      },
      {
        slug: 'casarett-and-doulls-essentials-of-toxicology',
        title: "Casarett & Doull's Essentials of Toxicology",
        coverSrc:
          '/nursing-demo/covers/pharmacy/casarett-and-doulls-essentials-of-toxicology.jpg',
      },
    ],
  },
  {
    slug: 'veterinary',
    label: 'Veterinary',
    books: [
      { slug: 'veterinary-anatomy', title: 'Veterinary Anatomy' },
      {
        slug: 'small-animal-internal-medicine',
        title: 'Small Animal Internal Medicine',
      },
      { slug: 'veterinary-pharmacology', title: 'Veterinary Pharmacology' },
      { slug: 'large-animal-surgery', title: 'Large Animal Surgery' },
      {
        slug: 'veterinary-clinical-pathology',
        title: 'Veterinary Clinical Pathology',
      },
    ],
  },
];

export const findMcGrawHillCategory = (
  slug: string | undefined
): McGrawHillCategory | undefined =>
  MCGRAW_HILL_CATEGORIES.find((category) => category.slug === slug);

/** Where a category's carousel opens: its working book, else the middle. */
export const initialBookIndex = (books: readonly McGrawHillBook[]): number => {
  const working = books.findIndex((book) => book.isWorking);
  return working >= 0 ? working : Math.floor(books.length / 2);
};

// Each book's place in its category, which picks its placeholder colour; a
// search or grid shows the same colour the full carousel does.
const TONE_INDEX_BY_SLUG = new Map(
  MCGRAW_HILL_CATEGORIES.flatMap((category) =>
    category.books.map((book, index) => [book.slug, index] as const)
  )
);

export const bookToneIndex = (slug: string): number =>
  TONE_INDEX_BY_SLUG.get(slug) ?? 0;
